// client/src/lib/hooks/use-user.ts
import { useQuery } from '@tanstack/react-query';
import { useFetchWithAuth } from '@/lib/fetchWithAuth';

export interface User {
  id?: string;
  name?: string;
  email?: string;
  isAuthenticated: boolean;
}

// Module-level cache to prevent duplicate in-flight requests
let cachedUser: User | null = null;
let inflightRequest: Promise<User> | null = null;
let lastFetchTime = 0;
const MIN_FETCH_INTERVAL = 30000; // 30 seconds minimum between fetches

export function primeCachedUser(user: User | null): void {
  cachedUser = user;
  lastFetchTime = Date.now();
}

export function clearCachedUser(): void {
  cachedUser = null;
  inflightRequest = null;
  lastFetchTime = 0;
}

export function useUser() {
  const fetchWithAuth = useFetchWithAuth();

  const { data, isLoading, error } = useQuery<User>({
    queryKey: ['user'],
    queryFn: async (): Promise<User> => {
      const defaultUser: User = { isAuthenticated: false };
      
      // Return cached user if we fetched recently
      const now = Date.now();
      if (cachedUser && (now - lastFetchTime) < MIN_FETCH_INTERVAL) {
        console.log('[useUser] Returning cached user (fetch interval not elapsed)');
        return cachedUser;
      }

      // Dedupe in-flight requests
      if (inflightRequest) {
        console.log('[useUser] Returning existing in-flight request');
        return inflightRequest;
      }

      console.log('[useUser] Fetching authenticated user from /api/auth/user');
      
      inflightRequest = (async (): Promise<User> => {
        try {
          const response = await fetchWithAuth('/api/auth/user');

          const contentType = response.headers.get('Content-Type') || '';
          const isJson = contentType.includes('application/json');

          if (!response.ok) {
            // Handle 429 rate limit - return cached user or safe default
            if (response.status === 429) {
              console.warn('[useUser] Rate limited (429), returning cached user');
              return cachedUser || { isAuthenticated: true };
            }

            if (!isJson) {
              throw new Error(`Server error: ${response.status} - Expected JSON but got ${contentType}`);
            }

            if (response.status === 401) {
              cachedUser = { isAuthenticated: false };
              lastFetchTime = now;
              return cachedUser;
            }

            const errorData = await response.json().catch(() => ({ message: response.statusText }));
            throw new Error(`Auth check failed: ${response.status} - ${errorData.message || response.statusText}`);
          }

          if (!isJson) {
            throw new Error(`Invalid response type: ${contentType}`);
          }

          const fetchedData = await response.json();
          const user: User = { ...fetchedData, isAuthenticated: true };
          cachedUser = user;
          lastFetchTime = now;
          return user;
        } finally {
          inflightRequest = null;
        }
      })();

      return inflightRequest;
    },
    retry: false,
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes (formerly cacheTime)
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  // Always return a non-null user object to avoid null checks elsewhere
  const safeUser: User = data || cachedUser || { isAuthenticated: false };

  return { user: safeUser, isLoading: isLoading && !cachedUser, error };
}
